import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCw, Sparkles, Trash2 } from 'lucide-react';

import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { toast } from 'sonner';

const SECTION_CONFIG = {
  support_impact: {
    category: 'support_impact',
    label: 'Impact Stories',
    singularLabel: 'Impact story',
    description: 'Stories that make the mission tangible for Support Partners without over-telling or polishing them.',
    emptyMessage: 'No impact stories published yet.',
    questions: [
      {
        key: 'who',
        prompt: 'Who did you connect with or hear about recently — a dad, a guide partner, or someone who reached out?',
      },
      {
        key: 'what_happened',
        prompt: 'What was going on for them? What were they struggling with, or what happened?',
      },
      {
        key: 'the_turn',
        prompt: 'What changed, or what happened as a result of being part of Dad Partners?',
      },
      {
        key: 'anonymize',
        prompt: 'Should we use their name, first name only, or keep them anonymous?',
      },
      {
        key: 'pillar',
        prompt:
          'Which of the four pillars does this story most connect to? Rest (Matthew 11:28–30) — called out of chaos and striving. Safety (1 John 4:18) — held, able to show up as you are. Brotherhood (Ecclesiastes 4:9–12) — belonging, a cord of three strands. Legacy (Deuteronomy 6:5–7) — significance, passing faith to the next generation.',
      },
    ],
  },
  support_narrative: {
    category: 'support_narrative',
    label: 'Mission Narrative',
    singularLabel: 'Mission narrative update',
    description: 'Mission notes, personal updates, and candid sustainability reflections that help trust grow instead of erode.',
    emptyMessage: 'No mission narrative posts published yet.',
    questions: [
      {
        key: 'on_mind',
        prompt: "What's been on your mind about the mission in the past week or two?",
      },
      {
        key: 'working',
        prompt: "What's working that you want to acknowledge or celebrate?",
      },
      {
        key: 'hard',
        prompt: "What's hard right now — something you're wrestling with or uncertain about?",
      },
      {
        key: 'want_them_to_feel',
        prompt: 'What do you want Support Partners to know or feel after reading this?',
      },
    ],
  },
};

const createComposerState = (mode = 'manual') => ({
  mode,
  title: '',
  content: '',
  answers: {},
  currentQuestionIndex: 0,
  generating: false,
  submitting: false,
});

const formatDate = (value) => {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return date.toLocaleDateString('en-US', {
    month: 'long',
    day: 'numeric',
    year: 'numeric',
  });
};

const previewContent = (value) => {
  const text = String(value || '').trim();
  if (!text) return 'No content yet.';
  return text.length > 190 ? `${text.slice(0, 187)}...` : text;
};

const SupportContentSection = ({ adminFetch, config }) => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [composer, setComposer] = useState(createComposerState());
  const [deletingId, setDeletingId] = useState(null);

  const hasDraft = Boolean(composer.title.trim() || composer.content.trim());
  const currentQuestion = config.questions[composer.currentQuestionIndex] || null;
  const currentAnswer = currentQuestion ? composer.answers[currentQuestion.key] || '' : '';

  const answeredQuestions = useMemo(
    () =>
      config.questions.filter((question, index) => (
        index < composer.currentQuestionIndex && String(composer.answers[question.key] || '').trim()
      )),
    [composer.answers, composer.currentQuestionIndex, config.questions]
  );

  const fetchPosts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await adminFetch(`/posts?category=${config.category}`);
      const payload = await response.json().catch(() => []);
      if (!response.ok) {
        throw new Error(payload?.detail || `Failed to load ${config.label.toLowerCase()}`);
      }
      setPosts(Array.isArray(payload) ? payload : []);
    } catch (error) {
      console.error(`Failed to load ${config.category} posts`, error);
      toast.error(error.message || `Failed to load ${config.label.toLowerCase()}`);
      setPosts([]);
    } finally {
      setLoading(false);
    }
  }, [adminFetch, config.category, config.label]);

  useEffect(() => {
    fetchPosts();
  }, [fetchPosts]);

  const resetInterview = () => {
    setComposer(createComposerState('ai'));
  };

  const toggleMode = () => {
    setComposer((current) => ({
      ...createComposerState(current.mode === 'manual' ? 'ai' : 'manual'),
      title: current.title,
      content: current.content,
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!composer.title.trim() || !composer.content.trim()) {
      toast.error('Title and content are required');
      return;
    }

    setComposer((current) => ({ ...current, submitting: true }));
    try {
      const response = await adminFetch('/posts', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          title: composer.title.trim(),
          content: composer.content.trim(),
          category: config.category,
          type: 'discussion',
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || `Failed to publish ${config.label.toLowerCase()}`);
      }

      toast.success(`${config.singularLabel} published`);
      setComposer(createComposerState());
      await fetchPosts();
    } catch (error) {
      console.error(`Failed to publish ${config.category}`, error);
      toast.error(error.message || `Failed to publish ${config.label.toLowerCase()}`);
    } finally {
      setComposer((current) => ({ ...current, submitting: false }));
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Delete this post?')) return;

    setDeletingId(postId);
    try {
      const response = await adminFetch(`/posts/${postId}`, { method: 'DELETE' });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || `Failed to delete post (${response.status})`);
      }
      toast.success('Post deleted');
      await fetchPosts();
    } catch (error) {
      console.error(`Failed to delete ${config.category} post`, error);
      toast.error(error.message || 'Failed to delete post');
    } finally {
      setDeletingId(null);
    }
  };

  const handleAdvanceInterview = async () => {
    if (!currentQuestion || !String(currentAnswer).trim()) {
      toast.error('Please answer the current question before moving on');
      return;
    }

    if (composer.currentQuestionIndex < config.questions.length - 1) {
      setComposer((current) => ({
        ...current,
        currentQuestionIndex: current.currentQuestionIndex + 1,
      }));
      return;
    }

    setComposer((current) => ({ ...current, generating: true }));
    try {
      const response = await adminFetch('/admin/support-content/draft', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: config.category,
          answers: composer.answers,
        }),
      });
      const payload = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(payload?.detail || `Failed to generate ${config.label.toLowerCase()} draft`);
      }

      setComposer((current) => ({
        ...current,
        title: payload.title || '',
        content: payload.content || '',
      }));
      toast.success('Draft generated');
    } catch (error) {
      console.error(`Failed to generate ${config.category} draft`, error);
      toast.error(error.message || 'Failed to generate draft');
    } finally {
      setComposer((current) => ({ ...current, generating: false }));
    }
  };

  const setCurrentAnswer = (value) => {
    if (!currentQuestion) return;
    setComposer((current) => ({
      ...current,
      answers: {
        ...current.answers,
        [currentQuestion.key]: value,
      },
    }));
  };

  return (
    <Card className="border border-gray-200 bg-white p-6 dark:border-[#2a2a32] dark:bg-[#1e1e24]">
      <div className="flex flex-col gap-4 border-b border-gray-200 pb-6 dark:border-[#2a2a32] lg:flex-row lg:items-start lg:justify-between">
        <div>
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">{config.label}</h3>
          <p className="mt-2 max-w-3xl text-sm leading-6 text-gray-600 dark:text-gray-400">{config.description}</p>
        </div>
        <div className="flex gap-2">
          <Button
            className="border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-[#2a2a32] dark:text-white dark:hover:bg-[#16161a]"
            onClick={fetchPosts}
            type="button"
            variant="outline"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </Button>
          <Button className="bg-aqua text-charcoal hover:bg-aqua-dark" onClick={toggleMode} type="button">
            <Sparkles className="h-4 w-4" />
            {composer.mode === 'manual' ? 'Help me draft this' : 'Write it myself'}
          </Button>
        </div>
      </div>

      <div className="mt-6 grid gap-6 xl:grid-cols-[1.05fr,0.95fr]">
        <div className="space-y-4">
          <div className="flex items-center justify-between">
            <h4 className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">Published posts</h4>
            <Badge className="border-aqua/30 bg-aqua/10 text-aqua">{posts.length}</Badge>
          </div>
          {loading ? (
            <div className="flex justify-center rounded-2xl border border-gray-200 bg-gray-50 py-12 dark:border-[#2a2a32] dark:bg-[#16161a]">
              <Loader2 className="h-8 w-8 animate-spin text-aqua" />
            </div>
          ) : posts.length === 0 ? (
            <div className="rounded-2xl border border-gray-200 bg-gray-50 px-4 py-8 text-sm text-gray-500 dark:border-[#2a2a32] dark:bg-[#16161a] dark:text-gray-400">
              {config.emptyMessage}
            </div>
          ) : (
            posts.map((post) => (
              <div className="rounded-2xl border border-gray-200 bg-gray-50 p-4 dark:border-[#2a2a32] dark:bg-[#16161a]" key={post.post_id}>
                <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                  <div className="min-w-0">
                    <p className="text-xs font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
                      {formatDate(post.created_at)}
                    </p>
                    <p className="mt-2 text-base font-semibold text-gray-900 dark:text-white">{post.title}</p>
                    <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-400">{previewContent(post.content)}</p>
                  </div>
                  <Button
                    className="border-red-200 text-red-600 hover:bg-red-50 dark:border-red-500/20 dark:text-red-300 dark:hover:bg-red-500/10"
                    disabled={deletingId === post.post_id}
                    onClick={() => handleDelete(post.post_id)}
                    type="button"
                    variant="outline"
                  >
                    {deletingId === post.post_id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                    Delete
                  </Button>
                </div>
              </div>
            ))
          )}
        </div>

        <form className="space-y-4 rounded-[1.8rem] border border-gray-200 bg-gray-50 p-5 dark:border-[#2a2a32] dark:bg-[#16161a]" onSubmit={handleSubmit}>
          <div>
            <p className="text-sm font-semibold uppercase tracking-[0.2em] text-gray-500 dark:text-gray-400">
              {composer.mode === 'manual' ? 'Manual compose' : 'AI-assisted compose'}
            </p>
            <p className="mt-2 text-sm leading-6 text-gray-600 dark:text-gray-400">
              {composer.mode === 'manual'
                ? 'Write directly, then publish when the note sounds human and ready.'
                : 'Answer the interview one question at a time, then turn it into an editable first draft.'}
            </p>
          </div>

          {composer.mode === 'manual' ? (
            <>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Title</p>
                <Input
                  className="border-gray-300 bg-white text-gray-900 dark:border-[#333642] dark:bg-[#121217] dark:text-white"
                  onChange={(event) => setComposer((current) => ({ ...current, title: event.target.value }))}
                  placeholder={`Title for this ${config.singularLabel.toLowerCase()}`}
                  value={composer.title}
                />
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Content</p>
                <Textarea
                  className="min-h-[220px] border-gray-300 bg-white text-gray-900 dark:border-[#333642] dark:bg-[#121217] dark:text-white"
                  onChange={(event) => setComposer((current) => ({ ...current, content: event.target.value }))}
                  placeholder="Write the post here"
                  value={composer.content}
                />
              </div>
            </>
          ) : hasDraft ? (
            <>
              <div className="rounded-2xl border border-aqua/20 bg-aqua/5 px-4 py-3 text-sm text-gray-700 dark:text-gray-300">
                Draft generated. Edit freely before publishing.
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Title</p>
                <Input
                  className="border-gray-300 bg-white text-gray-900 dark:border-[#333642] dark:bg-[#121217] dark:text-white"
                  onChange={(event) => setComposer((current) => ({ ...current, title: event.target.value }))}
                  value={composer.title}
                />
              </div>
              <div className="space-y-2">
                <p className="text-sm font-medium text-gray-800 dark:text-gray-200">Content</p>
                <Textarea
                  className="min-h-[220px] border-gray-300 bg-white text-gray-900 dark:border-[#333642] dark:bg-[#121217] dark:text-white"
                  onChange={(event) => setComposer((current) => ({ ...current, content: event.target.value }))}
                  value={composer.content}
                />
              </div>
              <button
                className="text-sm font-medium text-gray-500 underline-offset-4 hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-white"
                onClick={resetInterview}
                type="button"
              >
                Start over
              </button>
            </>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <Badge className="border-aqua/30 bg-aqua/10 text-aqua">
                  Question {composer.currentQuestionIndex + 1} of {config.questions.length}
                </Badge>
                <button
                  className="text-sm font-medium text-gray-500 underline-offset-4 hover:text-gray-700 hover:underline dark:text-gray-400 dark:hover:text-white"
                  onClick={resetInterview}
                  type="button"
                >
                  Start over
                </button>
              </div>

              {answeredQuestions.length > 0 ? (
                <div className="space-y-2 rounded-2xl border border-gray-200 bg-white px-4 py-3 dark:border-[#2a2a32] dark:bg-[#121217]">
                  {answeredQuestions.map((question) => (
                    <div key={question.key}>
                      <p className="text-xs font-semibold uppercase tracking-[0.16em] text-gray-500 dark:text-gray-400">{question.key}</p>
                      <p className="mt-1 text-sm text-gray-700 dark:text-gray-300">{composer.answers[question.key]}</p>
                    </div>
                  ))}
                </div>
              ) : null}

              {currentQuestion ? (
                <div className="space-y-2">
                  <p className="text-sm font-medium text-gray-800 dark:text-gray-200">{currentQuestion.prompt}</p>
                  <Textarea
                    className="min-h-[170px] border-gray-300 bg-white text-gray-900 dark:border-[#333642] dark:bg-[#121217] dark:text-white"
                    onChange={(event) => setCurrentAnswer(event.target.value)}
                    placeholder="Type or dictate your answer here"
                    value={currentAnswer}
                  />
                </div>
              ) : null}

              <div className="flex flex-wrap gap-2">
                {composer.currentQuestionIndex > 0 ? (
                  <Button
                    className="border-gray-300 text-gray-700 hover:bg-gray-100 dark:border-[#333642] dark:text-white dark:hover:bg-[#121217]"
                    onClick={() => setComposer((current) => ({ ...current, currentQuestionIndex: current.currentQuestionIndex - 1 }))}
                    type="button"
                    variant="outline"
                  >
                    Back
                  </Button>
                ) : null}
                <Button
                  className="bg-aqua text-charcoal hover:bg-aqua-dark"
                  disabled={composer.generating}
                  onClick={handleAdvanceInterview}
                  type="button"
                >
                  {composer.generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                  {composer.currentQuestionIndex === config.questions.length - 1 ? 'Generate draft' : 'Next'}
                </Button>
              </div>
            </>
          )}

          <div className="flex justify-end pt-2">
            <Button className="bg-aqua text-charcoal hover:bg-aqua-dark" disabled={composer.submitting || !composer.title.trim() || !composer.content.trim()} type="submit">
              {composer.submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
              Submit
            </Button>
          </div>
        </form>
      </div>
    </Card>
  );
};

const SupportPartnerContent = ({ adminFetch }) => {
  return (
    <div className="space-y-6">
      <Card className="border border-cyan-200 bg-gradient-to-br from-[#ecf9fb] via-white to-[#eef6f8] p-6 dark:border-aqua/20 dark:bg-gradient-to-br dark:from-[#141d20] dark:via-[#12161a] dark:to-[#11171a]">
        <p className="text-[11px] font-semibold uppercase tracking-[0.26em] text-cyan-700 dark:text-aqua">
          Support Partners flow
        </p>
        <h2 className="mt-3 text-2xl font-semibold text-slate-900 dark:text-white">
          Publish support-facing content without making it sound polished
        </h2>
        <p className="mt-3 max-w-3xl text-sm leading-6 text-slate-600 dark:text-gray-300">
          Use this page to manage the impact stories and mission narrative updates Support Partners will see in the
          portal. Drafts can start with Kevin&apos;s own writing or with an AI interview that turns raw answers into an
          editable first pass.
        </p>
      </Card>

      <SupportContentSection adminFetch={adminFetch} config={SECTION_CONFIG.support_impact} />
      <SupportContentSection adminFetch={adminFetch} config={SECTION_CONFIG.support_narrative} />
    </div>
  );
};

export default SupportPartnerContent;
